import { AwaitedNotification, DataTableColumn, DataType, useNotify } from '@globaleris/ui.library'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumGrupoDocumento } from '../../../../api'
import { EnumFileStateWithDispatch } from '../../../../api/Pioneer/enums/enumFileStateWithDispatch'
import { NotificationMessageIcons } from '../../../../components'
import { enumToSelectOptionArray, enumValueToKey } from '../../../../helpers'
import { useConfirmationDialog } from '../../../../hooks'
import useRetries from '../../../../hooks/useRetries'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { completeDataTableActions } from '../../regulatoryReports.helper'
import { regulatoryReportDownloadCard, RegulatoryReportsTypes } from '../../regulatoryReports.Model'
import {
  fetchReportFilesByGroupsListItems,
  generateRegulatoryReportAsync,
  getGenerateRegulatoryReport
} from '../../regulatoryReports.service'
import { ReportsContainer } from '../container/reportsContainer'
import { CardProps, FilterDates } from '../reportCard/reportCard'
import { ReportTable } from '../reportTable/reportTable'

export interface CoresReportsProps {
  commodity: number
  onGlobalNotificationChangeHandler: any
  onRemitTableChildFetching: any
}

export const CoresReports: React.FC<CoresReportsProps> = ({
  commodity,
  onGlobalNotificationChangeHandler,
  onRemitTableChildFetching
}) => {
  const [t] = useTranslation(['regulatoryReports', 'enums', 'storageHelper'])
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { error, awaited } = useNotify()

  const [tableFetching, setTableFetching] = useState<boolean>(false)
  const [tableData, setTableData] = useState<any>([])
  const columns: DataTableColumn[] = [
    {
      name: 'documentType',
      label: t('regulatoryReports:commodityPower.table.colType'),
      type: DataType.TEXT
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'fileName',
      label: t('regulatoryReports:commodityPower.table.colName'),
      type: DataType.TEXT
    },
    {
      name: 'fileDate',
      label: t('regulatoryReports:commodityPower.table.colDate'),
      type: DataType.DATE
    },
    {
      name: 'fileState',
      label: t('regulatoryReports:commodityPower.table.colState'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumFileStateWithDispatch,
        'enums:EnumFileStateWithDispatch'
      )
    }
  ]

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async () => {
      fetchCoresReports()
    },
    5000,
    {
      autoStart: false,
      immediate: true,
      onRetry: () => {
        onGlobalNotificationChangeHandler({
          icon: NotificationMessageIcons.LOADING,
          message: t('regulatoryReports:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                onGlobalNotificationChangeHandler({
                  message: t('regulatoryReports:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        startRetriesTimer()
                      }
                    },
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.close'),
                      onClick: () => {
                        onGlobalNotificationChangeHandler(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        onGlobalNotificationChangeHandler({
          icon: NotificationMessageIcons.INFO,
          message: t('regulatoryReports:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            },
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.close'),
              onClick: () => {
                onGlobalNotificationChangeHandler(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        onGlobalNotificationChangeHandler({
          icon: NotificationMessageIcons.ERROR,
          message: t('regulatoryReports:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            }
          ]
        })
      }
    }
  )

  const generateReport = async (
    dateFilter: FilterDates,
    reportType: RegulatoryReportsTypes,
    commodity: number
  ) => {
    var request = getGenerateRegulatoryReport(
      dateFilter.startDate,
      dateFilter.endDate,
      reportType,
      commodity
    )

    getConfirmation(
      ConfirmationDialogs.GENERATE_REPORT,
      t('confirmDialogs:GENERATE_REPORT.message', {
        startDate: moment(dateFilter.startDate).format('DD/MM/YYYY'),
        endDate: moment(dateFilter.endDate).format('DD/MM/YYYY')
      })
    ).then(async () => {
      closeConfirmation()

      awaited({
        title: t('regulatoryReports:notifications.generateReportAsync.title', {}),
        message: t('regulatoryReports:notifications.generateReportAsync.message'),
        keyInfo: reportType.toString()
      } as AwaitedNotification)

      generateRegulatoryReportAsync(request).then(async response => {
        if (response.success) {
          startRetriesTimer()
        } else {
          error('Error interno del Servidor. Comuníquese con el Soporte Técnico.')
        }
      })
    })
  }

  const handleClickCoresCard = (props: CardProps) => {
    generateReport(props.dates, RegulatoryReportsTypes.CORES, commodity)
  }

  const fetchCoresReports = async (): Promise<regulatoryReportDownloadCard[] | null> => {
    const groupDocuments: EnumGrupoDocumento[] = [
      enumValueToKey(EnumGrupoDocumento, EnumGrupoDocumento.IIRRCORESGas)
    ]
    return await fetchReportFilesByGroupsListItems(groupDocuments).then(async response => {
      if (response.success && response.data?.length > 0) {
        setTableData(
          response.data?.map((item: regulatoryReportDownloadCard) => {
            return completeDataTableActions(item)
          })
        )
        let updatedReportFiles: regulatoryReportDownloadCard[] = []
        response.data!.forEach((pendingReport: regulatoryReportDownloadCard) => {
          if (pendingReport.fileState === EnumFileStateWithDispatch.EnProceso) {
            updatedReportFiles.push(pendingReport)
          }
        })
        setTableFetching(false)
        return updatedReportFiles
      } else {
        setTableFetching(false)
        setTableData([])
        return null
      }
    })
  }

  useEffect(() => {
    fetchCoresReports()
  }, [])

  return (
    <ReportsContainer
      commodity={commodity}
      onGlobalNotificationChange={onGlobalNotificationChangeHandler}
      onClick={handleClickCoresCard}
      cards={[{ cardName: 'cores' }]}
      table={
        <ReportTable
          tableName="cores"
          isFetching={tableFetching}
          columns={columns}
          data={tableData}
          groupDocumentToFilter={[
            enumValueToKey(EnumGrupoDocumento, EnumGrupoDocumento.IIRRCNMCGas)
          ]}
        />
      }
    />
  )
}
